import { useEffect, useRef, useState } from 'react';
import { X, Volume2, VolumeX, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const STORAGE_KEY = 'homepage_popup_hole_zero_video_dismissed';

const LABELS = {
  ca: 'Més informació',
  es: 'Más información',
  en: 'More information',
  fr: "Plus d'informations"
};

const FLAGS = [
  { code: 'ca', flag: '🇦🇩', label: 'CAT' },
  { code: 'es', flag: '🇪🇸', label: 'ESP' },
  { code: 'en', flag: '🇬🇧', label: 'ENG' },
  { code: 'fr', flag: '🇫🇷', label: 'FRA' }
];

export const VideoPopup = ({ onClose }) => {
  const [muted, setMuted] = useState(true);
  const [videoLang, setVideoLang] = useState(null);
  const videoRef = useRef(null);
  const { language } = useLanguage();

  const lang = videoLang || (['ca', 'es', 'en', 'fr'].includes(language) ? language : 'ca');

  const close = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    onClose();
  };

  const goToFunnel = () => {
    window.open(`/curs-iniciacio-golf?lang=${lang}`, '_blank', 'noopener');
    onClose();
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const next = !muted;
    setMuted(next);
    if (videoRef.current) videoRef.current.muted = next;
  };

  const pickLang = (code) => {
    setVideoLang(code);
    setMuted(false);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in duration-300"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label="Hole Zero Experience"
      data-testid="homepage-popup-overlay"
    >
      <div
        className="relative w-auto max-h-[92vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          className="absolute -top-3 -right-3 md:-top-4 md:-right-4 z-10 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
          aria-label="Tancar"
          data-testid="homepage-popup-close"
        >
          <X className="w-5 h-5 text-[#1A4D2E]" />
        </button>

        <div className="relative rounded-lg overflow-hidden shadow-2xl">
          <video
            key={lang}
            ref={videoRef}
            src={`/videos/hole_zero_${lang}.mp4`}
            autoPlay
            muted={muted}
            loop
            playsInline
            onClick={goToFunnel}
            className="h-[65vh] md:h-[70vh] w-auto max-w-[90vw] object-contain cursor-pointer bg-black"
            data-testid="homepage-popup-video"
          />

          <button
            type="button"
            onClick={toggleMute}
            className="absolute top-3 left-3 z-10 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center transition-colors"
            aria-label={muted ? 'Activar so' : 'Silenciar'}
            data-testid="homepage-popup-mute-toggle"
          >
            {muted ? (
              <VolumeX className="w-5 h-5 text-white" />
            ) : (
              <Volume2 className="w-5 h-5 text-white" />
            )}
          </button>
        </div>

        <div className="mt-3 flex items-center gap-2" data-testid="homepage-popup-lang-flags">
          {FLAGS.map((f) => (
            <button
              key={f.code}
              type="button"
              onClick={() => pickLang(f.code)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide transition-all ${
                lang === f.code
                  ? 'bg-white text-[#1A4D2E] shadow-lg scale-105'
                  : 'bg-white/20 text-white hover:bg-white/35'
              }`}
              aria-label={`Vídeo en ${f.label}`}
              data-testid={`homepage-popup-lang-${f.code}`}
            >
              <span className="text-base leading-none">{f.flag}</span>
              {f.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={goToFunnel}
          className="mt-3 inline-flex items-center gap-2 px-8 py-3 bg-[#C5A253] hover:bg-[#b3913f] text-[#1A4D2E] font-bold uppercase tracking-wider text-sm rounded-full shadow-lg transition-colors"
          data-testid="homepage-popup-cta"
        >
          {LABELS[lang]}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const HomepagePopup = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const dismissed = window.sessionStorage.getItem(STORAGE_KEY);
    if (dismissed) return;
    const timer = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (_) {}
    setOpen(false);
  };

  if (!open) return null;

  return <VideoPopup onClose={handleClose} />;
};

export default HomepagePopup;
